import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useHistory } from 'react-router-dom';
import { CircleCheck, Keyboard, QrCode, Search, Star } from 'lucide-react';
import { Avatar, Button, Card, cn, Notice, Spinner } from '@tcrd/ui';
import { Page } from '../../components/Page';
import { QrScanner } from '../../components/QrScanner';
import { VehicleIllustration } from '../../components/VehicleIllustration';
import { useToast } from '../../components/toast';
import { api, errorMessage } from '../../lib/api';

export interface PublicDriver {
  id: string;
  firstName: string;
  lastName: string;
  photoUrl: string | null;
  rating: string;
  ratingCount: number;
  vehicle: { make: string; model: string; year: number; color: string; type: string; plate: string; photoUrl: string | null } | null;
  presence?: string;
  trips: number;
  /** Solo en "mi conductor". */
  phone?: string | null;
}

export const useMyDriver = (enabled = true) => useQuery({ queryKey: ['my-driver'], queryFn: () => api<{ driver: PublicDriver | null }>('/customers/me/driver'), enabled });

/** Saca el código de un QR o enlace del conductor (".../c/<código>") o lo toma tal cual si es solo el código. */
export function codeFrom(text: string): string | null {
  const t = text.trim();
  const m = /\/c\/([a-z0-9-]{3,40})\/?(?:[?#].*)?$/i.exec(t);
  if (m) return m[1]!.toLowerCase();
  return /^[a-z0-9-]{3,40}$/i.test(t) ? t.toLowerCase() : null;
}

function DriverRow({ d, selected, onSelect }: { d: PublicDriver; selected?: boolean; onSelect?: () => void }) {
  const body = (
    <>
      <Avatar src={d.photoUrl} firstName={d.firstName} lastName={d.lastName} size={52} />
      <span className="flex-1 min-w-0 text-left">
        <span className="block font-semibold truncate" data-person>{d.firstName} {d.lastName}</span>
        <span className="flex items-center gap-1 text-sm text-[var(--text-muted)]">
          <Star className="size-4 fill-current text-amber-500" aria-hidden /> {Number(d.rating) > 0 ? Number(d.rating).toFixed(1) : 'Nuevo'}
          {d.vehicle ? ` · ${d.vehicle.make} ${d.vehicle.model} ${d.vehicle.color}` : ''}
        </span>
      </span>
      {d.vehicle && (d.vehicle.photoUrl ? <img src={d.vehicle.photoUrl} alt="" className="h-11 w-16 rounded-lg object-cover" /> : <VehicleIllustration type={d.vehicle.type} color={d.vehicle.color} className="h-11 w-20" />)}
    </>
  );
  if (!onSelect) return <div className="flex items-center gap-3">{body}</div>;
  return (
    <button type="button" role="radio" aria-checked={!!selected} onClick={onSelect} className={cn('tap w-full flex items-center gap-3 rounded-2xl border p-3', selected ? 'border-brand-red bg-brand-red/5' : 'border-[var(--border)] bg-[var(--surface)]')}>
      {body}
    </button>
  );
}

/**
 * "Únete a tu conductor": el cliente que se registró sin el enlace de un conductor escanea el QR
 * de su conductor, escribe su código o elige un conductor de confianza de la lista.
 */
export function MyDriverPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const history = useHistory();
  const mine = useMyDriver();
  const [scanning, setScanning] = useState(false);
  const [typing, setTyping] = useState(false);
  const [code, setCode] = useState('');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [chosen, setChosen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);
  const hasDriver = !!mine.data?.driver;
  const options = useQuery({
    queryKey: ['driver-options', q],
    queryFn: () => api<PublicDriver[]>('/customers/me/driver-options', { query: { q: q || undefined } }),
    enabled: mine.isSuccess && !hasDriver,
    placeholderData: (prev) => prev,
  });

  const link = useCallback(
    async (body: { code?: string; driverId?: string }) => {
      setBusy(true);
      try {
        const r = await api<{ linked: boolean; driver: PublicDriver }>('/customers/me/driver', { body });
        qc.setQueryData(['my-driver'], { driver: r.driver });
        toast(r.linked ? `Listo: tu conductor es ${r.driver.firstName}` : 'Ya tienes un conductor asignado', r.linked ? 'success' : 'dark');
      } catch (e) {
        toast(errorMessage(e).includes('no encontr') || errorMessage(e).includes('Conductor') ? 'No encontramos ese conductor. Revisa el código.' : errorMessage(e), 'danger');
      } finally {
        setBusy(false);
      }
    },
    [qc, toast],
  );

  const onScan = useCallback(
    (text: string) => {
      setScanning(false);
      const c = codeFrom(text);
      if (!c) return toast('Ese código QR no es de un conductor de TuConductorRD', 'danger');
      void link({ code: c });
    },
    [link, toast],
  );

  if (mine.isLoading) return <Page title="Mi conductor" back="/app/home"><Spinner /></Page>;
  const d = mine.data?.driver;

  return (
    <Page title="Mi conductor" back="/app/home">
      {d ? (
        <div className="flex flex-col gap-4">
          <Card className="p-5 flex flex-col gap-4">
            <p className="text-sm text-[var(--text-muted)]">Vinculado con</p>
            <DriverRow d={d} />
            <Notice>
              <span className="flex items-start gap-2"><CircleCheck className="size-5 text-green-600 shrink-0" aria-hidden /> Tus solicitudes de viaje le llegan directamente a {d.firstName}.</span>
            </Notice>
          </Card>
          <Link to="/app/request"><Button block size="lg" variant="accent">Solicitar un viaje</Button></Link>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div>
            <h1 className="text-2xl font-bold">Únete a tu conductor</h1>
            <p className="text-[var(--text-muted)]">Vincúlate con tu conductor de confianza para solicitarle tus viajes de forma rápida y segura.</p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Button variant="accent" icon={<QrCode className="size-5" />} onClick={() => setScanning(true)} disabled={busy}>Escanear QR</Button>
            <Button variant="secondary" icon={<Keyboard className="size-5" />} onClick={() => setTyping((x) => !x)} disabled={busy}>Escribir código</Button>
          </div>
          <p className="text-xs text-center text-[var(--text-muted)] -mt-2">Escanea el código QR de tu conductor o escribe el código de su enlace.</p>
          {typing && (
            <form
              className="flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                const c = codeFrom(code);
                if (!c) return toast('Escribe el código o pega el enlace de tu conductor', 'danger');
                void link({ code: c });
              }}
            >
              <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Ej. carlos-7k2p o el enlace" autoCapitalize="none" autoFocus className="flex-1 min-w-0 h-12 rounded-2xl border border-[var(--border)] bg-[var(--surface)] px-4" aria-label="Código del conductor" />
              <Button type="submit" loading={busy}>Unirme</Button>
            </form>
          )}

          <div className="flex items-center gap-3 text-sm text-[var(--text-muted)]" aria-hidden>
            <span className="h-px flex-1 bg-[var(--border)]" /> o elige un conductor de confianza <span className="h-px flex-1 bg-[var(--border)]" />
          </div>

          <label className="flex items-center gap-2 h-12 rounded-2xl border border-[var(--border)] bg-[var(--surface)] px-3 focus-within:border-[var(--text)]">
            <Search className="size-5 text-[var(--text-muted)] shrink-0" aria-hidden />
            <span className="sr-only">Buscar conductor por nombre</span>
            <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar conductor por nombre" className="flex-1 min-w-0 bg-transparent outline-none text-base" autoComplete="off" />
          </label>
          {options.isLoading && <Spinner />}
          {options.data?.length === 0 && <p className="text-sm text-[var(--text-muted)]">{q ? `No hay conductores que coincidan con "${q}".` : 'Aún no hay conductores disponibles.'}</p>}
          <div className="flex flex-col gap-2" role="radiogroup" aria-label="Conductores">
            {options.data?.map((o) => <DriverRow key={o.id} d={o} selected={chosen === o.id} onSelect={() => setChosen(o.id)} />)}
          </div>

          <div className="sticky bottom-24 flex flex-col gap-2">
            <Button block size="lg" variant="accent" disabled={!chosen} loading={busy} onClick={() => chosen && void link({ driverId: chosen })}>Elegir como mi conductor</Button>
            <button className="tap text-sm underline text-[var(--text-muted)]" onClick={() => history.replace('/app/home')}>Ahora no</button>
          </div>
        </div>
      )}
      {scanning && <QrScanner onResult={onScan} onClose={() => setScanning(false)} />}
    </Page>
  );
}
